import AscCalendarMonthView from "./AscCalendarMonthView/AscCalendarMonthView";
import AscButton from "../AscButton/AscButton";
import styles from "./AscCalendarDatePicker.module.scss";
import { ClassicChevronLeftIcon, ClassicChevronRightIcon } from "../AscIcons";
import {
  CalendarDate,
  useCalendarDays,
} from "../../lib/helpers/hooks/useCalendarDays";
import { useEffect, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";

export interface AscCalendarDatePickerProps {
  value?: string[];
  onChange?: (dates?: string[]) => void;
  daysToSelect?: number;
  className?: string;
}

function AscCalendarDatePicker({
  daysToSelect = 1,
  onChange,
  value,
  className,
}: AscCalendarDatePickerProps) {
  const {
    goToPreviousMonths,
    goToNextMonths,
    calendarDaysForMonth,
    activeMonthLabel,
    weekdayLabels,
  } = useCalendarDays();
  const [selectedDays, setSelectedDays] = useState<CalendarDate[]>([]);

  function onDayClick(day: CalendarDate) {
    if (daysToSelect <= 0) {
      return;
    }

    if (
      selectedDays?.some(
        (date) => dayjs(date?.date)?.format() === dayjs(day?.date)?.format()
      )
    ) {
      const filteredDays = selectedDays?.filter(
        (date) => dayjs(date?.date)?.format() !== dayjs(day?.date)?.format()
      );
      setSelectedDays(filteredDays);
      onChange?.(filteredDays?.map((day) => dayjs(day?.date)?.format()));
      return;
    }
    if (selectedDays?.length >= daysToSelect) {
      return;
    }
    const newSelection = [...selectedDays, day];
    setSelectedDays(newSelection);
    onChange?.(newSelection?.map((day) => dayjs(day?.date)?.format()));
  }

  useEffect(() => {
    if (!value) {
      return;
    }
    setSelectedDays(
      value?.map((date) => ({
        date: dayjs(date).toDate(),
        dayLabel: dayjs(date).format("D"),
      }))
    );
  }, [value]);

  return (
    <div
      className={classNames(styles.ground, className)}
      data-testid="asc-calendar-date-picker"
    >
      <div className={styles.heading}>
        <AscButton
          size="small"
          variant={"link"}
          onClick={goToPreviousMonths}
          hideLinkIcon
          data-testid="asc-calendar-date-picker-previous-month-button"
        >
          <ClassicChevronLeftIcon className={styles.navigationIcon} />
        </AscButton>
        <div
          className={styles.monthLabel}
          data-testid="asc-calendar-date-picker-active-month-label"
        >
          {activeMonthLabel}
        </div>
        <AscButton
          size="small"
          variant={"link"}
          onClick={goToNextMonths}
          hideLinkIcon
          data-testid="asc-calendar-date-picker-next-month-button"
        >
          <ClassicChevronRightIcon className={styles.navigationIcon} />
        </AscButton>
      </div>

      <div className={styles.calendarContainer}>
        <AscCalendarMonthView
          calendarDaysForMonth={calendarDaysForMonth}
          weekdayLabels={weekdayLabels}
          onDayClick={onDayClick}
          selectedDays={selectedDays}
          maxSelected={daysToSelect === selectedDays?.length}
        />
      </div>
    </div>
  );
}

export default AscCalendarDatePicker;
